export * from './moods.js';
export * from './schemas.js';
export * from './realtime.js';
export * from './sse.js';
export * from './errors.js';
export * from './format.js';
export * from './api-client.js';
export * from './prompts.js';

import type { Plan } from './schemas.js';

/**
 * How many people a room holds, by plan. This is enforced by the API and quoted
 * on the pricing page, so it lives here rather than in either of them — the two
 * had already drifted apart once.
 */
export const ROOM_CAPACITY: Record<Plan, number> = { free: 4, muse: 20, studio: 50 };

/* ------------------------------------------------------------------ *
 * Features that are built but withheld
 * ------------------------------------------------------------------ */

/**
 * Surfaces that exist, ship and are tested, but are not baked out enough to
 * carry a tab yet.
 *
 * A flag being `false` hides the feature from every entry point — the desktop
 * rail, the phone tab bar, and any cross-link or CTA that points at it. It does
 * **not** unmount the route, the screen, the API or the Durable Object: anyone
 * holding `racksavant://room/ABC123` or `/app/rooms/ABC123` still lands where
 * they expect, because a link someone sent a friend is a promise.
 *
 * Every surface reads this one object, so flipping a single flag brings the
 * feature all the way back — navigation, links and all. Nothing else needs
 * touching.
 */
export const FEATURE_IDS = ['rooms', 'runway', 'savant'] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];

export const FEATURES: Record<FeatureId, boolean> = {
  /** Fitting rooms — live, ephemeral, friends reacting in real time. */
  rooms: false,
  /** The public Runway feed. */
  runway: false,
  /** The Savant — the stylist chat. */
  savant: false,
};

/** True when a withheld feature is showing again. Read this, never a literal. */
export function featureOn(id: FeatureId): boolean {
  return FEATURES[id];
}

export const RACKSAVANT = {
  name: 'RackSavant',
  tagline: 'Try on the feeling.',
  /** Free plan allowance, reset daily. */
  freeFittingsPerDay: 8,
  /** Max garments layered into a single look. */
  maxGarmentsPerLook: 6,
  /** Fitting rooms are ephemeral on purpose — the urgency is the point. */
  roomTtlMs: 1000 * 60 * 60 * 12,
  maxBodyShots: 12,
} as const;
